// FR-29, FR-33: `docs/design/links.html` Block 3 implemented verbatim --
// one shared repeatable-rows widget mounted by both admin forms
// (`impl-forms`), a card per link with a remove control, stacked
// "Подпись"/"Ссылка" fields, a dashed "+ Ещё ссылка" add control, and
// the block hint. Fully controlled: it owns no form state of its own --
// `rows`/`onChange` are the parent `useForm`'s own state, which is also
// what makes FR-32's "rows as typed" hold across a failed save's
// Inertia same-component redirect (the component never unmounts).
export interface LinkRow {
  label: string
  url: string
}

// `optional` is the host form's own existing tag text
// (`admin.item.optional` / `admin.stays.optional`), reused rather than a
// third copy under `links.*` (the plan's own ruling) -- every other
// field is this domain's own `links.*` namespace. `rowHeading` is the
// *raw*, un-interpolated `links.row_heading` template ("Ссылка %{n}" /
// "Link %{n}") -- FR-29's "N is the row's current visual position,
// renumbering when a row is removed" can only be computed client-side,
// as rows are added and removed with no server round trip, so this
// component performs the `%{n}` substitution itself (`#formatRowHeading`
// below) rather than receiving N pre-baked into the string.
export interface LinksFieldsetLabels {
  heading: string
  optional: string
  rowHeading: string
  remove: string
  add: string
  labelLabel: string
  urlLabel: string
  hint: string
}

interface LinksFieldsetProps {
  rows: LinkRow[]
  onChange: (rows: LinkRow[]) => void
  labels: LinksFieldsetLabels
  // FR-31: the stay form's own second hint ("Карта строится из адреса
  // сама — её сюда добавлять не нужно") -- `docs/design/stays.html`
  // Block 4 renders it joined onto the block hint inside the *same*
  // `<span>` ("hint. extraHint"), not a second line, so that join
  // happens here rather than at each of the two call sites. Absent (the
  // item form's own case) renders the block hint alone.
  extraHint?: string
  // R-5: dotted keys `Links::ReplaceFor` produces --
  // `links.<index>.label` / `links.<index>.url` -- keyed by the row's
  // *original submitted* index, which is also simply this component's
  // own current row index: FR-32 requires a failed save to re-render
  // every row exactly as typed, blanks included, so no row is ever
  // dropped or reordered between a submit and its error response.
  errors: Record<string, string | undefined>
}

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const URL_INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-sm text-gray-900"
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"
const HINT_CLASSES = "text-xs leading-4 text-gray-400"

// FR-29: "N" is the row's current *visual* position (1-based) -- a
// heading, not a stored identity. A plain, boring `String#replace`
// rather than a general-purpose interpolation helper: `%{n}` is the
// only placeholder this one template ever carries.
function formatRowHeading(template: string, n: number): string {
  return template.replace("%{n}", String(n))
}

function blankRow(): LinkRow {
  return { label: "", url: "" }
}

interface LinkCardProps {
  row: LinkRow
  index: number
  labels: LinksFieldsetLabels
  errors: Record<string, string | undefined>
  onChange: (index: number, row: LinkRow) => void
  onRemove: (index: number) => void
}

// One bordered card per row, Block 3's own shape verbatim. Split out of
// `LinksFieldset` for Metrics/MethodLength-style readability alone -- it
// owns no state of its own either, every field routes straight back
// through `onChange`/`onRemove`.
function LinkCard({ row, index, labels, errors, onChange, onRemove }: LinkCardProps) {
  const labelError = errors[`links.${index}.label`]
  const urlError = errors[`links.${index}.url`]

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-gray-200 p-3">
      <div className="flex items-center justify-between text-xs leading-4 text-gray-400">
        <span>{formatRowHeading(labels.rowHeading, index + 1)}</span>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-[13px] font-medium leading-[18px] text-red-700"
        >
          {labels.remove}
        </button>
      </div>
      <label className="flex flex-col gap-1.5">
        <span className={LABEL_TEXT_CLASSES}>{labels.labelLabel}</span>
        <input
          type="text"
          value={row.label}
          onChange={(event) => onChange(index, { ...row, label: event.target.value })}
          className={INPUT_CLASSES}
        />
        {labelError && <span className={ERROR_CLASSES}>{labelError}</span>}
      </label>
      <label className="flex flex-col gap-1.5">
        <span className={LABEL_TEXT_CLASSES}>{labels.urlLabel}</span>
        <input
          type="url"
          value={row.url}
          onChange={(event) => onChange(index, { ...row, url: event.target.value })}
          placeholder="https://"
          className={URL_INPUT_CLASSES}
        />
        {urlError && <span className={ERROR_CLASSES}>{urlError}</span>}
      </label>
    </div>
  )
}

// FR-29 (empty state): zero rows renders the heading plus the dashed add
// button alone -- no empty card, no special-casing here, since mapping
// over an empty `rows` array already draws nothing. FR-33: no
// drag-and-drop anywhere -- row order is authoring order, "Убрать"
// closes the gap by filtering the removed index out, "+ Ещё ссылка"
// only ever appends.
export default function LinksFieldset({
  rows, onChange, labels, extraHint, errors,
}: LinksFieldsetProps) {
  function updateRow(index: number, row: LinkRow) {
    onChange(rows.map((existing, i) => (i === index ? row : existing)))
  }

  function removeRow(index: number) {
    onChange(rows.filter((_, i) => i !== index))
  }

  function addRow() {
    onChange([...rows, blankRow()])
  }

  return (
    <div role="group" aria-labelledby="links-heading" className="flex flex-col gap-1.5">
      <span id="links-heading" className={`flex items-baseline gap-2 ${LABEL_TEXT_CLASSES}`}>
        {labels.heading} <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
      </span>
      <div className="flex flex-col gap-2.5">
        {rows.map((row, index) => (
          // R-10 (assumed): rows carry no server-issued id -- a save
          // replaces the whole set, so array position is this widget's
          // own row identity too.
          <LinkCard
            key={index}
            row={row}
            index={index}
            labels={labels}
            errors={errors}
            onChange={updateRow}
            onRemove={removeRow}
          />
        ))}
        <button
          type="button"
          onClick={addRow}
          className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-gray-300 text-sm font-medium text-accent"
        >
          {labels.add}
        </button>
      </div>
      <span className={HINT_CLASSES}>
        {extraHint ? `${labels.hint}. ${extraHint}` : labels.hint}
      </span>
    </div>
  )
}
